import { CalendarDaysIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ActionIcon, Badge, Box, Button, Checkbox, Group, rem, Text } from "@mantine/core";
import { SVGProps } from "react";
import { useTranslation } from "../../../../hooks/useTranslation";
import { allOnPageSelected } from "../../../../utils/selectionManager";
import { EmployeeOverviewListItem } from "../../models/employeeOverview.model";

const IconUserPlus = (props: SVGProps<SVGSVGElement>) => <UserPlusIcon {...props} />;
const IconCalendarDays = (props: SVGProps<SVGSVGElement>) => <CalendarDaysIcon {...props} />;
const IconX = (props: SVGProps<SVGSVGElement>) => <XMarkIcon {...props} />;

interface SelectionModeToolbarProps {
    selectionMode: boolean;
    onEnterSelectionMode: () => void;
    pageEmployees: EmployeeOverviewListItem[];
    selectedIds: Set<number>;
    onSelectAllOnPage: () => void;
    onDeselectAllOnPage: () => void;
    onClearAll: () => void;
    onCancel: () => void;
    onSetAvailability: () => void;
    hasResults: boolean;
    loading: boolean;
}

/**
 * Same always-visible "Select" entry point / selected-count badge / cancel shell as the CV list's
 * shipped toolbar (BulkOperationsManager's inline block — not the orphaned, never-imported
 * cv-list/.../SelectionModeToolbar.tsx, which was superseded by that inline duplicate and isn't
 * the one users actually see). Renders only the one action that's shipped (FS §7) — no disabled
 * placeholders for the roadmap items (notify, email, export) that come later.
 */
export function SelectionModeToolbar({
    selectionMode,
    onEnterSelectionMode,
    pageEmployees,
    selectedIds,
    onSelectAllOnPage,
    onDeselectAllOnPage,
    onClearAll,
    onCancel,
    onSetAvailability,
    hasResults,
    loading,
}: SelectionModeToolbarProps) {
    const { t } = useTranslation();
    const pageIds = pageEmployees.map((e) => e.employeeId);
    const selectedCount = selectedIds.size;
    const allPageSelected = allOnPageSelected(selectedIds, pageIds);
    const somePageSelected = !allPageSelected && pageIds.some((id) => selectedIds.has(id));

    if (!hasResults && !loading) {
        return null;
    }

    if (!selectionMode) {
        return (
            <Group justify="flex-end" mt="sm" mb={4}>
                <Button
                    variant="light"
                    color="brand"
                    size="xs"
                    leftSection={<IconUserPlus style={{ width: rem(16), height: rem(16) }} />}
                    onClick={onEnterSelectionMode}
                    disabled={loading}
                    aria-label={t("EMPLOYEEOVERVIEW_BULK_SELECT_ENTRY_ARIA", undefined, "Select employees")}
                >
                    {t("EMPLOYEEOVERVIEW_BULK_SELECT_BUTTON", undefined, "Select")}
                </Button>
            </Group>
        );
    }

    return (
        <Box
            mt="sm"
            mb="xs"
            style={{
                borderRadius: 8,
                border: "1px solid var(--mantine-color-orange-5)",
                background: "var(--mantine-color-orange-3)",
                padding: "8px 12px",
            }}
        >
            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                <Group gap="sm" align="center">
                    <Checkbox
                        checked={allPageSelected}
                        indeterminate={somePageSelected}
                        onChange={() => (allPageSelected ? onDeselectAllOnPage() : onSelectAllOnPage())}
                        label={<Text size="sm" fw={600} c="dark.8">{t("EMPLOYEEOVERVIEW_BULK_SELECT_PAGE_LABEL", undefined, "Select page")}</Text>}
                        aria-label={
                            allPageSelected
                                ? t("EMPLOYEEOVERVIEW_BULK_DESELECT_PAGE_ARIA", undefined, "Deselect all on this page")
                                : t("EMPLOYEEOVERVIEW_BULK_SELECT_PAGE_ARIA", undefined, "Select all on this page")
                        }
                    />
                    {selectedCount > 0 && (
                        <Group gap={4} align="center">
                            <Badge color="brand.6" variant="filled" size="md">
                                {t("EMPLOYEEOVERVIEW_BULK_SELECTED_COUNT", { count: selectedCount.toLocaleString() }, `${selectedCount.toLocaleString()} selected`)}
                            </Badge>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={onClearAll}
                                aria-label={t("EMPLOYEEOVERVIEW_BULK_CLEAR_ALL_ARIA", undefined, "Clear all selections")}
                            >
                                <IconX style={{ width: rem(14), height: rem(14) }} />
                            </ActionIcon>
                        </Group>
                    )}
                </Group>

                <Group gap="xs" align="center">
                    <Button
                        size="compact-sm"
                        color="brand.6"
                        variant="filled"
                        leftSection={<IconCalendarDays style={{ width: rem(14), height: rem(14) }} />}
                        onClick={onSetAvailability}
                        disabled={selectedCount === 0}
                        aria-label={
                            selectedCount > 0
                                ? t("EMPLOYEEOVERVIEW_BULK_SET_AVAILABILITY_ARIA", { count: selectedCount.toString() }, `Set availability for ${selectedCount} selected`)
                                : t("EMPLOYEEOVERVIEW_BULK_SET_AVAILABILITY_DISABLED_ARIA", undefined, "Select employees to set availability")
                        }
                    >
                        {t("EMPLOYEEOVERVIEW_BULK_SET_AVAILABILITY_BUTTON", undefined, "Set availability")}
                    </Button>
                    <Button
                        size="compact-sm"
                        variant="subtle"
                        color="dark.8"
                        leftSection={<IconX style={{ width: rem(14), height: rem(14) }} />}
                        onClick={onCancel}
                        aria-label={t("EMPLOYEEOVERVIEW_BULK_CANCEL_ARIA", undefined, "Cancel selection mode")}
                    >
                        {t("EMPLOYEEOVERVIEW_BULK_CANCEL_BUTTON", undefined, "Cancel")}
                    </Button>
                </Group>
            </Group>
        </Box>
    );
}
